// View Component: FAQ bölümü
import React, { useState } from "react";
import TiltCard from "../UI/TiltCard";
import { useIntersectionObserver } from "../../hooks/useLandingPage";

const Contact = () => {
  const [sectionRef, isVisible] = useIntersectionObserver({ threshold: 0.2 });
  const [openFAQ, setOpenFAQ] = useState(null);
  const faqData = [
    {
      id: 1,
      question: "Who is this email summarizer app for?",
      answer:
        "It's ideal for professionals, students, customer support agents, and anyone overwhelmed by a high volume of emails. If you want to save time and never miss important details, this tool is for you.",
    },
    {
      id: 2,
      question: "Can I use the app for free?",
      answer:
        "Yes! You can try the core features for free with our Basic plan. We also offer a 14-day free trial of Premium features with no credit card required.",
    },
    {
      id: 3,
      question: "Is my email data safe?",
      answer:
        "Absolutely. We use end-to-end encryption and never store your email content. All summaries are processed securely, and we’re fully compliant with GDPR and other privacy standards.",
    },
    {
      id: 4,
      question: "Does the app read all my emails?",
      answer:
        "No. You control what the app can access. It only processes emails you permit and only for the purpose of generating summaries. Nothing is shared or used beyond that scope.",
    },
    {
      id: 5,
      question: "How can I get help or support?",
      answer:
        "Our team is here to support you via email, live chat, and an in-depth help center. You can also join our user community to ask questions and share feedback.",
    },
  ];

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const sectionStyles = {
    padding: "var(--spacing-8xl) 0",
    background:
      "linear-gradient(135deg, #FFFFFF 0%, rgba(240, 253, 250, 0.3) 100%)",
    position: "relative",
    overflow: "hidden",
  };

  const containerStyles = {
    maxWidth: "900px",
    margin: "0 auto",
    padding: "0 var(--spacing-lg)",
    position: "relative",
    zIndex: 2,
  };

  const headerStyles = {
    textAlign: "center",
    marginBottom: "var(--spacing-6xl)",
  };

  const titleStyles = {
    fontFamily: "var(--font-secondary)",
    fontSize: "42px",
    fontWeight: "800",
    color: "var(--gray-900)",
    marginBottom: "var(--spacing-lg)",
    letterSpacing: "-0.5px",
  };

  const subtitleStyles = {
    fontSize: "19px",
    color: "var(--gray-700)",
    maxWidth: "700px",
    margin: "0 auto",
    lineHeight: "1.6",
    fontWeight: "500",
  };

  const faqContainerStyles = {
    display: "flex",
    flexDirection: "column",
    gap: "var(--spacing-lg)",
  };

  const FAQItem = ({ faq, index }) => {
    const isOpen = openFAQ === faq.id;

    const itemStyles = {
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      opacity: isVisible ? 1 : 0,
      transition: `all 600ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 100}ms`,
    };

    const questionStyles = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      padding: "var(--spacing-2xl)",
      cursor: "pointer",
      border: "none",
      background: "transparent",
      textAlign: "left",
    };

    const questionTextStyles = {
      fontSize: "18px",
      fontWeight: "600",
      color: "var(--gray-900)",
      fontFamily: "var(--font-secondary)",
      lineHeight: "1.4",
      flex: 1,
      marginRight: "var(--spacing-lg)",
    };

    const iconContainerStyles = {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      background: isOpen ? "var(--color-primary)" : "rgba(148, 163, 184, 0.1)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 300ms ease",
      transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
    };

    const iconStyles = {
      fontSize: "16px",
      color: isOpen ? "white" : "var(--gray-600)",
      fontWeight: "600",
    };

    const answerStyles = {
      padding: isOpen
        ? "0 var(--spacing-2xl) var(--spacing-2xl) var(--spacing-2xl)"
        : "0",
      maxHeight: isOpen ? "200px" : "0",
      overflow: "hidden",
      transition: "all 400ms cubic-bezier(0.4, 0, 0.2, 1)",
      opacity: isOpen ? 1 : 0,
    };

    const answerTextStyles = {
      fontSize: "16px",
      color: "var(--gray-700)",
      lineHeight: "1.7",
      fontFamily: "var(--font-primary)",
    };

    return (
      <TiltCard
        variant="base"
        style={itemStyles}
        rotateAmplitude={4}
        scaleOnHover={1.01}
      >
        <button onClick={() => toggleFAQ(faq.id)} style={questionStyles}>
          <span style={questionTextStyles}>{faq.question}</span>
          <div style={iconContainerStyles}>
            <span style={iconStyles}>+</span>
          </div>
        </button>

        <div style={answerStyles}>
          <p style={answerTextStyles}>{faq.answer}</p>
        </div>
      </TiltCard>
    );
  };

  return (
    <section style={sectionStyles} id="faq" ref={sectionRef}>
      {/* Hafif Aurora efekti */}
      <div
        style={{
          position: "absolute",
          top: "0",
          right: "0",
          width: "50%",
          height: "100%",
          background: `
          repeating-linear-gradient(100deg, transparent 0%, transparent 7%, rgba(255,255,255,0.3) 10%, rgba(255,255,255,0.3) 12%, transparent 16%),
          repeating-linear-gradient(100deg, rgba(240,253,250,0.1) 10%, rgba(248,250,252,0.08) 15%, rgba(241,245,249,0.08) 20%, rgba(243,244,246,0.05) 25%, rgba(240,253,250,0.1) 30%)
        `,
          backgroundSize: "300% 200%",
          opacity: "0.2",
          animation: "aurora 80s linear infinite",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      <div style={containerStyles}>
        <div style={headerStyles}>
          <h2 style={titleStyles}>Frequently Asked Questions</h2>
          <p style={subtitleStyles}>
            Answers to your questions about Inbox Zero AI
          </p>
        </div>

        <div style={faqContainerStyles}>
          {faqData.map((faq, index) => (
            <FAQItem key={faq.id} faq={faq} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Contact;
